import { KnowledgeProduct } from './KnowledgeProduct'
/** Shared actual-catalog picker for item questions and intended retail targets. */
import { useState } from 'react'
import type { KnowledgeCatalogItem } from '@testfeed/integration-contracts'
import { storeKnowledgeCopy as copy } from '@testfeed/product-language'
import { useStoreKnowledge } from '../../hooks/useStoreKnowledge'
import type { ShopifyTokenSource } from '../../shopifyApiFetch'
import { trackShopifyAction, trackShopifyRefetch } from '../../shopifyUiTracking'
export function knowledgeItemLabel(item: KnowledgeCatalogItem) {
  if (item.subject.variantId) return `${item.productTitle ?? copy.unnamed} · ${item.title ?? copy.variant}`
  return `${item.title ?? copy.unnamed} · ${copy.product}`
}
export function KnowledgeItemPicker({
  shopify,
  onSelect,
  retail = false,
}: {
  shopify: ShopifyTokenSource
  onSelect: (item: KnowledgeCatalogItem, origin: 'suggested' | 'manual') => void
  retail?: boolean
}) {
  const [draft, setDraft] = useState('')
  const [search, setSearch] = useState('')
  const [suggestedOnly, setSuggestedOnly] = useState(!retail)
  const [after, setAfter] = useState('')
  const [visibleCount, setVisibleCount] = useState(5)
  const query = useStoreKnowledge(shopify, { search, suggestedOnly, after })
  const runSearch = () => {
    setVisibleCount(5)
    setSearch(draft)
    setAfter('')
    setSuggestedOnly(false)
  }
  const toggle = () => {
    setVisibleCount(5)
    setSuggestedOnly(!suggestedOnly)
    setAfter('')
    setSearch('')
    setDraft('')
  }
  let toggleLabel = copy.suggested as string
  if (suggestedOnly) toggleLabel = copy.browse
  let toggleControl = null
  if (!retail)
    toggleControl = (
      <s-button onClick={() => trackShopifyAction(shopify, 'select-filter', toggle)}>{toggleLabel}</s-button>
    )
  let content
  if (query.isPending) content = <s-paragraph>{copy.loading}</s-paragraph>
  else if (query.error)
    content = (
      <s-banner tone="critical">
        {copy.failed}
        <s-button onClick={() => void trackShopifyRefetch(shopify, () => query.refetch())}>{copy.retry}</s-button>
      </s-banner>
    )
  else {
    let more = null
    if (visibleCount < query.data.catalog.length)
      more = (
        <s-button onClick={() => trackShopifyAction(shopify, 'select-page', () => setVisibleCount(visibleCount + 5))}>
          {copy.more}
        </s-button>
      )
    else if (query.data.next)
      more = (
        <s-button
          onClick={() =>
            trackShopifyAction(shopify, 'select-page', () => {
              setVisibleCount(5)
              setAfter(query.data.next!)
            })
          }
        >
          {copy.more}
        </s-button>
      )
    let empty = null
    if (!query.data.catalog.length) empty = <s-paragraph>{copy.noItems}</s-paragraph>
    content = (
      <s-stack gap="base">
        {empty}
        {query.data.catalog.slice(0, visibleCount).map((item) => (
          <s-stack key={JSON.stringify(item.subject)} gap="small">
            <KnowledgeProduct item={item} />
            <s-button
              onClick={() =>
                trackShopifyAction(shopify, retail ? 'select-target' : 'select-item', () =>
                  onSelect(item, suggestedOnly ? 'suggested' : 'manual'),
                )
              }
            >
              {copy.chooseProduct}
            </s-button>
          </s-stack>
        ))}
        {more}
      </s-stack>
    )
  }
  let label = copy.search as string
  if (retail) label = copy.targetSearch
  return (
    <s-stack gap="base">
      <s-text-field label={label} value={draft} onInput={(e) => setDraft(e.currentTarget.value)} />
      <s-stack direction="inline" gap="base">
        <s-button onClick={() => trackShopifyAction(shopify, 'search-items', runSearch)}>{copy.searchAction}</s-button>
        {toggleControl}
      </s-stack>
      {content}
    </s-stack>
  )
}
